import { create } from 'zustand';
import { fetchMessages } from '@/services/conversationService';
import {
  fetchEntitiesByConversationIdPaginated,
  isConversationEntitiesFetchError,
} from '@/services/entityService';
import { CNSMessage } from '@/models/cns';
import { transformMessagesWithDeduplication, applyThreadHumanToolCallDedupe, stripNonAssistantStatus } from '@/lib/messageUtils';
import { hasPendingHumanToolCalls } from '@/lib/humanToolCallUtils';
import {
  reconcileEntityBackedToolCalls,
  type ToolCallHydrationIssue,
} from '@/lib/hydrateMissingToolCallsFromEntities';
import { ExtendedThreadMessageLike } from '@/types/message';

/** Stable empty reference for Zustand selectors (React 19 useSyncExternalStore). */
export const EMPTY_CONVERSATION_MESSAGES: ExtendedThreadMessageLike[] = [];
export const EMPTY_TOOL_CALL_HYDRATION_ISSUES: ToolCallHydrationIssue[] = [];

export type ConversationMessagesLoadResult = {
  messagesLoaded: boolean;
  entitiesLoaded: boolean;
  /** Present for atomic refetches; true only when the combined read replaced local state. */
  authoritativeReplacementApplied?: boolean;
};

interface MessageStore {
  // State: messages by conversation ID
  messagesByConversation: Record<string, ExtendedThreadMessageLike[]>;
  loadingByConversation: Record<string, boolean>;
  isRunningByConversation: Record<string, boolean>;
  /** Advances on local turn ownership or message mutations so stale reads cannot replace them. */
  mutationEpochByConversation: Record<string, number>;
  entityLoadErrorByConversation: Record<string, {
    message: string;
    retriable: boolean;
    status?: number;
  } | undefined>;
  toolCallHydrationIssuesByConversation: Record<string, ToolCallHydrationIssue[]>;
  
  // Actions
  setMessages: (conversationId: string, messages: ExtendedThreadMessageLike[]) => void;
  addMessage: (conversationId: string, message: ExtendedThreadMessageLike) => void;
  upsertMessage: (conversationId: string, message: ExtendedThreadMessageLike) => void;
  updateMessage: (conversationId: string, messageId: string, updates: Partial<ExtendedThreadMessageLike>) => void;
  loadMessages: (
    conversationId: string,
    options?: { force?: boolean; requireEntities?: boolean },
  ) => Promise<ConversationMessagesLoadResult>;
  setIsRunning: (conversationId: string, isRunning: boolean) => void;
  clearMessages: (conversationId: string) => void;
  
  // Getters
  getMessages: (conversationId: string) => ExtendedThreadMessageLike[];
  isLoading: (conversationId: string) => boolean;
  isRunning: (conversationId: string) => boolean;
  getEntityLoadError: (conversationId: string) => {
    message: string;
    retriable: boolean;
    status?: number;
  } | undefined;
  getToolCallHydrationIssues: (conversationId: string) => ToolCallHydrationIssue[];
}

function projectConversationMessages(
  rawMessages: CNSMessage[],
): ExtendedThreadMessageLike[] {
  // The legacy projection remains deduped. Modern raw content stays intact until holder
  // provenance chooses the canonical origin/part.
  const transformedMessages = transformMessagesWithDeduplication(rawMessages);

  // Use the rawMessage shell when available while retaining the projected content/status.
  return transformedMessages.map(({ parentId, message }, index) => {
    const originalMsg = rawMessages[index];

    if (originalMsg?.rawMessage) {
      const role = (originalMsg.rawMessage.role ?? message.role)?.toLowerCase();
      const rawContent = Array.isArray(originalMsg.rawMessage.content)
        ? originalMsg.rawMessage.content
        : message.content;
      const base = {
        ...originalMsg.rawMessage,
        content: rawContent,
        parentId,
      };
      if (role === "assistant") {
        return stripNonAssistantStatus({
          ...base,
          status: hasPendingHumanToolCalls(rawContent)
            ? { type: "requires-action", reason: "tool-calls" }
            : message.status ?? originalMsg.rawMessage.status,
        });
      }
      return stripNonAssistantStatus(base);
    }

    return stripNonAssistantStatus({
      id: message.id,
      role: message.role as "user" | "assistant" | "system",
      content: message.content,
      createdAt: message.createdAt,
      ...(message.role === "assistant" ? { status: message.status } : {}),
      metadata: message.metadata,
      parentId,
    });
  }) as ExtendedThreadMessageLike[];
}

export const useMessageStore = create<MessageStore>((set, get) => ({
  // Initial state
  messagesByConversation: {},
  loadingByConversation: {},
  isRunningByConversation: {},
  mutationEpochByConversation: {},
  entityLoadErrorByConversation: {},
  toolCallHydrationIssuesByConversation: {},
  
  // Set messages for a conversation
  setMessages: (conversationId: string, messages: ExtendedThreadMessageLike[]) => {
    set((state) => ({
      messagesByConversation: {
        ...state.messagesByConversation,
        [conversationId]: applyThreadHumanToolCallDedupe(messages),
      },
      mutationEpochByConversation: {
        ...state.mutationEpochByConversation,
        [conversationId]:
          (state.mutationEpochByConversation[conversationId] ?? 0) + 1,
      },
    }));
  },
  
  // Add a message to a conversation
  addMessage: (conversationId: string, message: ExtendedThreadMessageLike) => {
    set((state) => {
      const next = [...(state.messagesByConversation[conversationId] || []), message];
      return {
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: applyThreadHumanToolCallDedupe(next),
        },
        mutationEpochByConversation: {
          ...state.mutationEpochByConversation,
          [conversationId]:
            (state.mutationEpochByConversation[conversationId] ?? 0) + 1,
        },
      };
    });
  },

  upsertMessage: (conversationId: string, message: ExtendedThreadMessageLike) => {
    set((state) => {
      const current = state.messagesByConversation[conversationId] || [];
      const existingIndex = current.findIndex((candidate) => candidate.id === message.id);
      const next =
        existingIndex >= 0
          ? current.map((candidate, index) => (index === existingIndex ? message : candidate))
          : [...current, message];
      return {
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: applyThreadHumanToolCallDedupe(next),
        },
        mutationEpochByConversation: {
          ...state.mutationEpochByConversation,
          [conversationId]:
            (state.mutationEpochByConversation[conversationId] ?? 0) + 1,
        },
      };
    });
  },
  
  // Update a message in a conversation
  updateMessage: (conversationId: string, messageId: string, updates: Partial<ExtendedThreadMessageLike>) => {
    set((state) => {
      const messages = state.messagesByConversation[conversationId] || [];
      const updatedMessages = messages.map((msg) =>
        msg.id === messageId ? { ...msg, ...updates } : msg
      );
      return {
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: applyThreadHumanToolCallDedupe(updatedMessages),
        },
        mutationEpochByConversation: {
          ...state.mutationEpochByConversation,
          [conversationId]:
            (state.mutationEpochByConversation[conversationId] ?? 0) + 1,
        },
      };
    });
  },
  
  // Load messages from backend. Skips if already loaded for this conversation unless force is true.
  // Use force: true when (re-)entering a conversation so we refetch from server (e.g. after streaming or switching back).
  loadMessages: async (
    conversationId: string,
    options?: { force?: boolean; requireEntities?: boolean },
  ) => {
    const state = get();
    if (state.loadingByConversation[conversationId]) {
      return {
        messagesLoaded: false,
        entitiesLoaded: false,
        ...(options?.requireEntities
          ? { authoritativeReplacementApplied: false }
          : {}),
      };
    }
    if (!options?.force && state.messagesByConversation[conversationId] !== undefined) {
      return {
        messagesLoaded: true,
        entitiesLoaded: state.entityLoadErrorByConversation[conversationId] === undefined,
        ...(options?.requireEntities
          ? { authoritativeReplacementApplied: false }
          : {}),
      };
    }
    const loadMutationEpoch =
      state.mutationEpochByConversation[conversationId] ?? 0;

    set((state) => ({
      loadingByConversation: {
        ...state.loadingByConversation,
        [conversationId]: true,
      },
    }));

    const [messagesResult, entitiesResult] = await Promise.allSettled([
      fetchMessages(conversationId, 0, 1000),
      fetchEntitiesByConversationIdPaginated(conversationId, 0, 1000),
    ]);

    let threadMessages: ExtendedThreadMessageLike[] | undefined;
    let hydrationIssues: ToolCallHydrationIssue[] | undefined;
    let entityToolCallsReconciled = false;

    if (messagesResult.status === 'fulfilled') {
      try {
        threadMessages = projectConversationMessages(messagesResult.value);

        if (entitiesResult.status === 'fulfilled') {
          let hydration = reconcileEntityBackedToolCalls(
            threadMessages,
            entitiesResult.value.content,
          );
          if (hydration.issues.length > 0) {
            try {
              // Messages and holders are independent reads. A holder can become visible just
              // before terminal Message persistence. One bounded exact-conversation reread lets
              // the backend-authored rawMessage.content[] supply the durable part position.
              const refreshedRawMessages = await fetchMessages(
                conversationId,
                0,
                1000,
              );
              hydration = reconcileEntityBackedToolCalls(
                projectConversationMessages(refreshedRawMessages),
                entitiesResult.value.content,
              );
            } catch (error) {
              console.error(
                'Error reloading conversation messages for tool-card order recovery:',
                error,
              );
            }
          }
          hydrationIssues = hydration.issues;
          entityToolCallsReconciled = true;
          // Never synthesize a position for a provenanced part. An unresolved issue leaves the
          // current safe thread intact and is surfaced through the existing recovery notice.
          threadMessages =
            hydrationIssues.length === 0
              ? hydration.messages
              : undefined;
        }
        if (threadMessages !== undefined && !entityToolCallsReconciled) {
          threadMessages = applyThreadHumanToolCallDedupe(threadMessages);
        }
      } catch (error) {
        threadMessages = undefined;
        hydrationIssues = undefined;
        console.error('Error projecting conversation messages:', error);
      }
    } else {
      console.error('Error loading conversation messages:', messagesResult.reason);
    }

    if (entitiesResult.status === 'rejected') {
      console.error('Error loading conversation entities:', entitiesResult.reason);
    }

    let authoritativeReplacementApplied = false;
    set((state) => {
      const streamOwnsTurn = state.isRunningByConversation[conversationId] === true;
      const mutationEpochUnchanged =
        (state.mutationEpochByConversation[conversationId] ?? 0) ===
        loadMutationEpoch;
      const completeAuthoritativeRead =
        threadMessages !== undefined &&
        (!options?.requireEntities || entitiesResult.status === 'fulfilled');
      const messageReplacement =
        streamOwnsTurn || !mutationEpochUnchanged || !completeAuthoritativeRead
          ? undefined
          : threadMessages;
      const canReplaceMessages = messageReplacement !== undefined;
      authoritativeReplacementApplied = canReplaceMessages;
      const entityError =
        entitiesResult.status === 'rejected' &&
        isConversationEntitiesFetchError(entitiesResult.reason)
          ? {
              message: entitiesResult.reason.message,
              retriable: entitiesResult.reason.retriable,
              ...(entitiesResult.reason.status === undefined
                ? {}
                : { status: entitiesResult.reason.status }),
            }
          : undefined;

      return {
        ...(canReplaceMessages
          ? {
              messagesByConversation: {
                ...state.messagesByConversation,
                [conversationId]: messageReplacement,
              },
              // Clear stale streaming state only after a successful message read has
              // replaced the thread. Holder availability is tracked independently.
              isRunningByConversation: {
                ...state.isRunningByConversation,
                [conversationId]: false,
              },
            }
          : {}),
        loadingByConversation: {
          ...state.loadingByConversation,
          [conversationId]: false,
        },
        entityLoadErrorByConversation: {
          ...state.entityLoadErrorByConversation,
          // A local message mutation or a new load attempt is not proof that holders
          // are available. Only this holder request may clear its prior error.
          [conversationId]:
            entitiesResult.status === 'fulfilled'
              ? undefined
              : entityError ?? state.entityLoadErrorByConversation[conversationId],
        },
        ...(!streamOwnsTurn && mutationEpochUnchanged && hydrationIssues !== undefined
          ? {
              toolCallHydrationIssuesByConversation: {
                ...state.toolCallHydrationIssuesByConversation,
                [conversationId]: hydrationIssues,
              },
            }
          : {}),
      };
    });

    return {
      messagesLoaded: threadMessages !== undefined,
      entitiesLoaded: entitiesResult.status === 'fulfilled',
      ...(options?.requireEntities
        ? { authoritativeReplacementApplied }
        : {}),
    };
  },
  
  // Marks local turn ownership from optimistic user append through terminal assistant state.
  // Refresh and realtime reconciliation use this as a mutation barrier, not only a spinner flag.
  setIsRunning: (conversationId: string, isRunning: boolean) => {
    set((state) => {
      const current = state.isRunningByConversation[conversationId] ?? false;
      if (current === isRunning) return state;
      return {
        isRunningByConversation: {
          ...state.isRunningByConversation,
          [conversationId]: isRunning,
        },
        mutationEpochByConversation: {
          ...state.mutationEpochByConversation,
          [conversationId]:
            (state.mutationEpochByConversation[conversationId] ?? 0) + 1,
        },
      };
    });
  },
  
  // Clear messages for a conversation
  clearMessages: (conversationId: string) => {
    set((state) => {
      const restMessages = { ...state.messagesByConversation };
      const restLoading = { ...state.loadingByConversation };
      const restRunning = { ...state.isRunningByConversation };
      const restEntityErrors = { ...state.entityLoadErrorByConversation };
      const restHydrationIssues = {
        ...state.toolCallHydrationIssuesByConversation,
      };
      delete restMessages[conversationId];
      delete restLoading[conversationId];
      delete restRunning[conversationId];
      delete restEntityErrors[conversationId];
      delete restHydrationIssues[conversationId];
      return {
        messagesByConversation: restMessages,
        loadingByConversation: restLoading,
        isRunningByConversation: restRunning,
        entityLoadErrorByConversation: restEntityErrors,
        toolCallHydrationIssuesByConversation: restHydrationIssues,
        // Keep a tombstone epoch so an in-flight read cannot resurrect the cleared thread.
        mutationEpochByConversation: {
          ...state.mutationEpochByConversation,
          [conversationId]:
            (state.mutationEpochByConversation[conversationId] ?? 0) + 1,
        },
      };
    });
  },
  
  // Get messages for a conversation
  getMessages: (conversationId: string) => {
    return get().messagesByConversation[conversationId] ?? EMPTY_CONVERSATION_MESSAGES;
  },
  
  // Check if messages are loading for a conversation
  isLoading: (conversationId: string) => {
    return get().loadingByConversation[conversationId] || false;
  },
  
  // Check if assistant is running for a conversation
  isRunning: (conversationId: string) => {
    return get().isRunningByConversation[conversationId] || false;
  },

  getEntityLoadError: (conversationId: string) =>
    get().entityLoadErrorByConversation[conversationId],

  getToolCallHydrationIssues: (conversationId: string) =>
    get().toolCallHydrationIssuesByConversation[conversationId] ?? EMPTY_TOOL_CALL_HYDRATION_ISSUES,
}));
